import { InputFileProcessor } from './input-field/InputFileProcessor';
import {
  FieldMappings,
  InputFileUploadResult,
  PreviousFieldMappings,
} from './models';
import { toInputLayoutModel } from './utils';
import { ErrorsAndWarningReporter } from './ErrorsAndWarningReporter';
import { ElementWithFeedbackContainer } from './input-field/ElementWithFeedbackContainer';
import { FormLayout } from '../../../quest/program-wizard/common';
import { generateId } from '@/app/core/utils';
import {
  useDataPart,
  useDataStore,
  useStepContext,
} from '@/app/core/components/step-wizard';
import {
  InputFileProps,
  ModuleInputProps,
} from '@/app/features/products/common/components/input-props';
import {
  InputFileDetails,
  InputFileSelector,
} from '@/app/features/products/common/components/file-selection';
import { FrontEndProcessingStepModel } from '@/app/features/products/common/utils';
import { Table } from 'antd';
import { useState, useEffect, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers';
import { Controller, useForm } from 'react-hook-form';
import {
  HasFieldDimensions,
  InputLayoutModel,
  asFieldsWithDimensionsContainerAscendArichives,
} from '@ssar-app/domain';
import { Alert, FormLabel, Modal, Spin } from '@ssar-app/uikit';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  HStack,
  Input,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { FaInfoCircle } from 'react-icons/fa/index.js';

const { Column } = Table;

type CustomFrontEndProcessingStepModel = FrontEndProcessingStepModel & {
  fieldsInfo: {
    fieldsErrors?: {
      addressMinimums?: string;
      nameMinimums?: string;
      addressCombinations?: string;
      nameCombinations?: string;
    };
  };
};

export const InputFileInformationSelection = (
  props: ModuleInputProps<InputFileProps>,
) => {
  const { stepKey, validator } = props;
  const {
    productName,
    inputFileSelectionOptions,
    inputLabelsMap,
    isLoading: showLoader,
  } = props.moduleProps;
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data } = useDataStore();
  const { isStepActive, onStepDeactivate } = useStepContext();
  const [dataPartState, setDataPartState] =
    useDataPart<FrontEndProcessingStepModel>(stepKey);
  const [frontEndProcessingInfo, setFrontEndProcessingInfo] = useState<
    Partial<FrontEndProcessingStepModel>
  >(dataPartState.data || {});
  const [isValidForm, setIsValidForm] = useState<boolean>(false);

  const formMethods = useForm<CustomFrontEndProcessingStepModel>({
    defaultValues: frontEndProcessingInfo,
    mode: 'onChange',
    context: {},
    resolver: yupResolver(validator, {
      abortEarly: false,
    }),
  });
  const { errors, control, formState, setValue, watch, trigger } = formMethods;

  onStepDeactivate(() => {
    return new Promise((_resolve, _reject) => {
      trigger(['inputQuantity', 'clientInputFileName']).finally(() => {
        updateState();
      });
      _resolve(true);
    });
  });

  const updateState = useCallback(() => {
    setDataPartState((currentState) => {
      const newState = {
        ...currentState,
        isValid: isValidForm,
        isDirty: formState.isDirty,
        data: {
          ...currentState.data,
          ...frontEndProcessingInfo,
        },
      };
      return newState;
    });
  }, [formState, frontEndProcessingInfo, setDataPartState, isValidForm]);

  useEffect(() => {
    if (frontEndProcessingInfo && validator) {
      validator
        .validate(frontEndProcessingInfo)
        .then(() => {
          setIsValidForm(true);
        })
        .catch((_error) => {
          setIsValidForm(false);
        });
    }
  }, [frontEndProcessingInfo, validator]);

  useEffect(() => {
    updateState();
  }, [isValidForm, updateState]);

  const [isLoading, setIsLoading] = useState(false);
  const [showMlNotification, setShowMlNotification] = useState(false);

  const [inputFileDetails, setInputFileDetails] = useState<InputFileDetails>();
  const [showFileProcessorModal, setShowFileProcessorModal] = useState(false);
  const [isRemoteFile, setIsRemoteFile] = useState(true);
  const [selectedFieldMappings, setSelectedFieldMappings] = useState<
    FieldMappings[]
  >(() => {
    const toReturn = (frontEndProcessingInfo.inputLayout || []).reduce(
      (acc, curr, index) => {
        const recordIndex = acc.findIndex(
          (a) =>
            a.field ===
            (curr.clientHeaders === ''
              ? index.toString()
              : curr.clientHeaders || ''),
        );
        const toInsert: FieldMappings = {
          field:
            curr.clientHeaders === ''
              ? index.toString()
              : curr.clientHeaders || '',
          headerNameWithLength: {
            name: curr.fieldName,
            fieldLength: curr.length,
          },
        };
        if (recordIndex === -1) {
          acc.push(toInsert);
        } else {
          acc[recordIndex] = toInsert;
        }
        return acc;
      },
      [] as FieldMappings[],
    );
    return toReturn;
  });

  const previousFieldMappings = selectedFieldMappings.reduce((acc, curr) => {
    const recordIndex = acc.findIndex((a) => a.field === curr.field);
    const toInsert: PreviousFieldMappings = {
      field: curr.field,
      previousHeader: curr.headerNameWithLength?.name,
    };
    if (recordIndex === -1) {
      acc.push(toInsert);
    } else {
      acc[recordIndex] = toInsert;
    }
    return acc;
  }, [] as PreviousFieldMappings[]);

  const mappedFields: HasFieldDimensions<InputLayoutModel>[] =
    asFieldsWithDimensionsContainerAscendArichives(
      watch('inputLayout', []),
    ).fields;
  const isSSNFieldFound = mappedFields
    .map((a) => (a.clientHeaders || a.fieldName || '').toLowerCase())
    .includes('ssn');

  const minimumsCombinationsErrors: string[] = [
    errors?.fieldsInfo?.fieldsErrors?.addressCombinations?.type?.toString() ||
      null,
    errors?.fieldsInfo?.fieldsErrors?.nameCombinations?.type.toString() || null,
    errors?.fieldsInfo?.fieldsErrors?.addressMinimums?.type.toString() || null,
    errors?.fieldsInfo?.fieldsErrors?.nameMinimums?.type.toString() || null,
  ]
    .map((a) => (a || '').toString())
    .filter((a) => a.length > 0);

  const onFileUploadSuccess = useCallback(
    (uploadResult: InputFileUploadResult): void => {
      setIsLoading(false);
      setInputFileDetails(undefined);
      setSelectedFieldMappings(uploadResult.fieldMappings);

      setValue(
        'inputQuantity',
        uploadResult?.recordsExpected || frontEndProcessingInfo.inputQuantity,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setValue('clientInputFileName', uploadResult.clientFileName, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('experianInputFileName', uploadResult.experianFileName, {
        shouldDirty: true,
        shouldValidate: true,
      });

      const inputLayoutFields = toInputLayoutModel(
        uploadResult.fieldMappings,
        !!uploadResult.hasHeaders,
      );
      const newInfo = {
        ...frontEndProcessingInfo,
        inputQuantity:
          uploadResult?.recordsExpected || frontEndProcessingInfo.inputQuantity,
        clientInputFileName: uploadResult.clientFileName,
        experianInputFileName: uploadResult.experianFileName,
        inputHeader: uploadResult.hasHeaders,
        inputLayout: inputLayoutFields,
        intentType: inputFileDetails?.intentType || 'Default',
      };
      setFrontEndProcessingInfo(newInfo);

      formMethods.clearErrors('inputLayout');
      setValue('inputLayout', inputLayoutFields, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [formMethods, setValue, frontEndProcessingInfo, inputFileDetails],
  );

  useEffect(() => {
    if (frontEndProcessingInfo) {
      setValue('inputLayout', frontEndProcessingInfo.inputLayout, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [frontEndProcessingInfo, setValue]);

  useEffect(() => {
    if (frontEndProcessingInfo) {
      updateState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontEndProcessingInfo]);

  if (!isStepActive) {
    return null;
  }

  return (
    <Spin
      spinning={isLoading || showLoader}
      tip='Loading input file information...'
    >
      <Flex direction='column' w='full'>
        <form>
          <FormLayout header={'File Details'}>
            <Controller
              control={control}
              name='experianInputFileName'
              defaultValue={
                frontEndProcessingInfo.experianInputFileName || null
              }
            />
            <SimpleGrid columns={1} width={'full'} gap={4}>
              <Alert
                isOpen={data?.outputLayout?.isCustomized}
                type='warning'
                variant='left-accent'
                background={'white'}
                description={
                  <HStack color={'orange.700'}>
                    <FaInfoCircle />
                    <Text>
                      You have selected a Custom output layout for this job. If
                      you delete your input file or upload a new input file, the
                      output layout will be reset to the Standard output layout.
                    </Text>
                  </HStack>
                }
                showCloseButton={false}
                showIcon={false}
              />
              <Box>
                <FormControl
                  isInvalid={errors.inputQuantity ? true : false}
                  isRequired={false}
                >
                  <Grid templateColumns='repeat(5,1fr)'>
                    <GridItem justifyContent='end' display='flex'>
                      <FormLabel>Records Expected:</FormLabel>
                    </GridItem>
                    <GridItem>
                      <Controller
                        name='inputQuantity'
                        control={control}
                        defaultValue={
                          frontEndProcessingInfo.inputQuantity || null
                        }
                        render={({ value, onChange }) => (
                          <Input
                            type='number'
                            onChange={(e) => {
                              onChange(e.target.value);
                              const newModel = {
                                ...frontEndProcessingInfo,
                                inputQuantity: Number(e.target.value),
                              };
                              setValue(
                                'inputQuantity',
                                newModel.inputQuantity,
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );
                              setFrontEndProcessingInfo(newModel);
                            }}
                            defaultValue={value || null}
                            value={value}
                            spellCheck={false}
                          />
                        )}
                      ></Controller>
                      <FormErrorMessage>
                        {errors.inputQuantity?.message}
                      </FormErrorMessage>
                    </GridItem>
                  </Grid>
                </FormControl>
              </Box>
              <Box>
                <FormControl
                  isInvalid={errors.clientInputFileName ? true : false}
                  isRequired={true}
                >
                  <Grid templateColumns='repeat(5,1fr)'>
                    <GridItem justifyContent='end' display='flex'>
                      <FormLabel>Client File Name:</FormLabel>
                    </GridItem>
                    <GridItem>
                      <Controller
                        name='clientInputFileName'
                        control={control}
                        defaultValue={
                          frontEndProcessingInfo?.clientInputFileName || ''
                        }
                        render={({ value }) => (
                          <Flex display={value ? 'flex' : ''}>
                            <Text alignSelf={'center'}>{value}</Text>
                            <InputFileSelector
                              onFileClicked={() => {
                                setIsLoading(true);
                              }}
                              onCancel={() => {
                                setIsLoading(false);
                              }}
                              subCode={data?.subCode}
                              setIsRemoteFile={setIsRemoteFile}
                              isCompact={
                                !!frontEndProcessingInfo.clientInputFileName
                              }
                              onFileSelected={(details) => {
                                setShowMlNotification(true);
                                setInputFileDetails(details);
                                setShowFileProcessorModal(true);
                                setValue('delimiter', details.fieldDelimiter, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                setValue(
                                  'detectedHeaders',
                                  details.headersInfo.detectedHeaders,
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  },
                                );
                                setIsLoading(true);
                                // setValue('delimiter', details.);
                              }}
                              onSuccess={(uploadResult) => {
                                onFileUploadSuccess(uploadResult);
                              }}
                              inputLabelsMap={inputLabelsMap}
                              inputFileSelectionOptions={
                                inputFileSelectionOptions
                              }
                              productName={productName}
                              isModalVisible={isModalVisible}
                              setIsModalVisible={setIsModalVisible}
                            />
                          </Flex>
                        )}
                      ></Controller>
                      <FormErrorMessage>
                        {errors.clientInputFileName?.message
                          ? `${errors.clientInputFileName?.message}. Please select the input file to generate the file name.`
                          : null}
                      </FormErrorMessage>
                    </GridItem>
                  </Grid>
                </FormControl>
              </Box>
              <Box>
                <Controller
                  control={control}
                  name='inputLayout'
                  defaultValue={mappedFields || []}
                />
                <Flex mt={6}>
                  {mappedFields.length > 0 && (
                    <Grid templateColumns='repeat(5,1fr)'>
                      <GridItem justifyContent='end' display='flex'>
                        <FormLabel>File Layout:</FormLabel>
                      </GridItem>
                      <GridItem colSpan={4} display='flex' flexDirection='row'>
                        <Grid templateColumns='repeat(6,1fr)' gap={4}>
                          <GridItem colSpan={3}>
                            <Table
                              bordered
                              dataSource={mappedFields}
                              pagination={false}
                              size='small'
                              // className="auto-width-table"
                              rowKey={() => generateId()}
                            >
                              <Column
                                dataIndex='clientHeaders'
                                title='Client Header'
                                render={(text, row, index) => {
                                  const localError =
                                    formState.errors.inputLayout?.[index]
                                      ?.clientHeaders?.message;
                                  return localError ? (
                                    <ElementWithFeedbackContainer
                                      errors={[localError]}
                                    >
                                      <Alert
                                        type='error'
                                        description={text}
                                        backgroundColor={'#e87a655e'}
                                        border={'1px solid #d62b1a'}
                                        isOpen
                                        showCloseButton={false}
                                        showIcon={false}
                                      ></Alert>
                                    </ElementWithFeedbackContainer>
                                  ) : (
                                    text
                                  );
                                }}
                              ></Column>
                              <Column
                                dataIndex='fieldName'
                                title='Experian Header'
                                render={(text, row, index) => {
                                  const localError =
                                    formState.errors.inputLayout?.[index]
                                      ?.fieldName?.message;
                                  return localError ? (
                                    <ElementWithFeedbackContainer
                                      errors={[localError]}
                                    >
                                      <Alert
                                        type='error'
                                        description={text}
                                        backgroundColor={'#e87a655e'}
                                        border={'1px solid #d62b1a'}
                                        isOpen
                                        showCloseButton={false}
                                        showIcon={false}
                                      ></Alert>
                                    </ElementWithFeedbackContainer>
                                  ) : (
                                    text
                                  );
                                }}
                              ></Column>
                              <Column
                                dataIndex='length'
                                title='Length'
                                align='center'
                              ></Column>
                              <Column
                                dataIndex='begin'
                                title='Begin'
                                align='center'
                              ></Column>
                              <Column
                                dataIndex='end'
                                title='End'
                                align='center'
                              ></Column>
                            </Table>
                          </GridItem>
                          <GridItem colSpan={3}>
                            <ErrorsAndWarningReporter
                              errors={minimumsCombinationsErrors}
                              isSSNMapped={isSSNFieldFound}
                              hideSuccessMessages={true}
                              fieldErrors={[]}
                            />
                          </GridItem>
                        </Grid>
                      </GridItem>
                    </Grid>
                  )}
                </Flex>
              </Box>
            </SimpleGrid>
            {[
              'nameMinimums',
              'addressMinimums',
              'nameCombinations',
              'addressCombinations',
            ].map((errorType) => {
              return (
                <Controller
                  key={errorType}
                  control={control}
                  name={`fieldsInfo.fieldsErrors.${errorType}`}
                  defaultValue={null}
                />
              );
            })}

            {showFileProcessorModal && inputFileDetails && (
              <Modal
                header='Define File Layout'
                isOpen={showFileProcessorModal}
                footer={null}
                contentProps={{ maxW: '95vw' }}
                scrollBehavior='outside'
                onClose={() => {
                  setInputFileDetails(undefined);
                  setShowFileProcessorModal(false);
                  setIsLoading(false);
                }}
              >
                {isRemoteFile && (
                  <Modal
                    header='File Header Mapping'
                    isOpen={showMlNotification}
                    contentProps={{ maxW: '31vw' }}
                    okText='Continue'
                    cancelText='Cancel'
                    showCancel
                    bodyProps={{ pb: 6 }}
                    onOk={() => {
                      setShowMlNotification(false);
                    }}
                    onClose={() => {
                      setShowMlNotification(false);
                      setInputFileDetails(undefined);
                      setShowFileProcessorModal(false);
                      setIsLoading(false);
                    }}
                  >
                    <Text>
                      We automatically mapped standard Experian headers to the
                      ones you provided in your file.
                    </Text>
                    <div>
                      <Text as='b'>
                        Before accepting the layout,{' '}
                        <Text as='i'>
                          please verify that all of your column headers are
                          mapped correctly to the correct Experian headers.
                        </Text>
                      </Text>
                    </div>
                  </Modal>
                )}
                <InputFileProcessor
                  productName={productName}
                  setIsRemoteFile={setIsRemoteFile}
                  tmsNumber={data?.productInput?.productDetails?.accountNumber}
                  subCode={data?.subCode}
                  inputLabelsMap={inputLabelsMap}
                  previousFieldMappings={previousFieldMappings}
                  onAfterViewDisplayed={() => {
                    setIsLoading(false);
                  }}
                  details={inputFileDetails}
                  isRemoteFile={isRemoteFile}
                  onNewFileSelected={(fileDetails, currentFieldMappings) => {
                    setShowFileProcessorModal(false);
                    setShowMlNotification(true);
                    setInputFileDetails(fileDetails);
                    setValue('delimiter', fileDetails.fieldDelimiter, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setSelectedFieldMappings(currentFieldMappings);
                    console.log('Forcing Re-Render');
                    setShowFileProcessorModal(true);
                    // setFileProcessorComponentKey(generateId());
                  }}
                  onSuccess={(uploadResult) => {
                    // if (onFileSelected) {
                    //   onFileSelected();
                    // }
                    onFileUploadSuccess(uploadResult);
                  }}
                  inputFileSelectionOptions={inputFileSelectionOptions}
                />
              </Modal>
            )}
          </FormLayout>
        </form>
      </Flex>
    </Spin>
  );
};
